import type { ChartDatapoint } from '../../../core/types/chart-data.js';
import { makeAccessor } from '../../../core/utils/makeAccessor.js';
import {
  CHILDREN_COUNT_FIELD,
  DEPTH_FIELD,
  END_FIELD,
  SELF_VALUE_FIELD,
  START_FIELD,
  VALUE_FIELD,
} from '../constants.js';

const toFiniteNumber = (raw: unknown): number => {
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
};

type OrderingAccessors = {
  spanIdAccessor: string;
  parentIdAccessor: string;
  x0Accessor: string;
  x1Accessor: string;
  /**
   * Subtracted from each span's `x0`/`x1` when emitting `START_FIELD` /
   * `END_FIELD`, so the visible axis starts at zero. Defaults to `0`.
   */
  offset?: number;
  selfValueAccessor?: string;
};

type Entry = {
  span: ChartDatapoint;
  index: number;
  x0: number;
};

type Frame = { span: ChartDatapoint; depth: number };

/**
 * Traverses the span forest in DFS pre-order (parent before children, siblings
 * sorted by `x0` with original index as tie-breaker) and returns a flat list
 * where each span carries its `depth`, `childrenCount`, and axis-friendly
 * `START_FIELD` / `END_FIELD` / `VALUE_FIELD`. Zero-width spans are widened
 * by 1 in `END_FIELD` only so they remain paintable on the canvas; the raw
 * `x1` accessor field is left untouched so consumer reads stay truthful.
 *
 * Numeric x0/x1 are parsed once per span and cached on the internal `Entry`,
 * so the sort comparator does not re-run `Number()` at every comparison.
 * Traversal is iterative to avoid stack overflows on deep flame graphs.
 */
export function orderSpansHierarchically(
  spans: ChartDatapoint[],
  {
    spanIdAccessor,
    parentIdAccessor,
    x0Accessor,
    x1Accessor,
    offset = 0,
    selfValueAccessor,
  }: OrderingAccessors,
): ChartDatapoint[] {
  const readSpanId = makeAccessor(spanIdAccessor);
  const readParentId = makeAccessor(parentIdAccessor);
  const readX0 = makeAccessor(x0Accessor);
  const readX1 = makeAccessor(x1Accessor);
  const readSelfValue = makeAccessor(selfValueAccessor);

  const spanIds = new Set<unknown>();
  for (const span of spans) {
    spanIds.add(readSpanId(span));
  }

  const childrenMap = new Map<unknown, Entry[]>();
  const roots: Entry[] = [];

  for (let index = 0; index < spans.length; index++) {
    const span = spans[index];
    const entry: Entry = {
      span,
      index,
      x0: Number(readX0(span)),
    };
    const parentId = readParentId(span);
    const isRoot =
      parentId === null || parentId === undefined || !spanIds.has(parentId);

    if (isRoot) {
      roots.push(entry);
      continue;
    }

    const siblings = childrenMap.get(parentId);
    if (siblings) {
      siblings.push(entry);
    } else {
      childrenMap.set(parentId, [entry]);
    }
  }

  const byX0AndIndex = (a: Entry, b: Entry) => a.x0 - b.x0 || a.index - b.index;

  roots.sort(byX0AndIndex);
  for (const children of childrenMap.values()) {
    children.sort(byX0AndIndex);
  }

  const result: ChartDatapoint[] = [];
  const stack: Frame[] = [];
  for (let i = roots.length - 1; i >= 0; i--) {
    stack.push({ span: roots[i].span, depth: 0 });
  }

  for (let frame = stack.pop(); frame !== undefined; frame = stack.pop()) {
    const { span, depth } = frame;
    const spanId = readSpanId(span);
    const rawX0 = Number(readX0(span) ?? 0);
    const rawX1 = Number(readX1(span) ?? 0);
    const widenedX1 = rawX0 === rawX1 ? rawX1 + 1 : rawX1;
    result.push({
      ...span,
      [DEPTH_FIELD]: depth,
      [CHILDREN_COUNT_FIELD]: childrenMap.get(spanId)?.length ?? 0,
      [START_FIELD]: rawX0 - offset,
      [END_FIELD]: widenedX1 - offset,
      [VALUE_FIELD]: rawX1 - rawX0,
      ...(readSelfValue !== undefined && {
        [SELF_VALUE_FIELD]: toFiniteNumber(readSelfValue(span)),
      }),
    });

    const children = childrenMap.get(spanId);
    if (!children) {
      continue;
    }
    for (let i = children.length - 1; i >= 0; i--) {
      stack.push({ span: children[i].span, depth: depth + 1 });
    }
  }

  return result;
}
